import {useEffect, useState} from "react"
import {updateTeletechToken, updateToken, useGetAuthToken} from "../store/api/AuthSlice"
import {useAuth0} from "@auth0/auth0-react"
import {useDispatch} from "../store/Store"

export const useGetAndFetchToken = () => {
    const token = useGetAuthToken()
    const {
        getAccessTokenSilently,
        isAuthenticated,
        isLoading: isAuthLoading,
        loginWithRedirect,
        user
    } = useAuth0()
    const dispatch = useDispatch()
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (isAuthLoading) {
            return
        }

        if (!isAuthenticated) {
            setLoading(false)
            return
        }

        let isMounted = true

        const syncTokens = async () => {
            const fetchToken = async (audience: string | undefined): Promise<string | null> => {
                if (!audience) {
                    return null
                }

                try {
                    const response = await getAccessTokenSilently({
                        detailedResponse: true,
                        authorizationParams: {audience}
                    })

                    return response.access_token
                } catch (error) {
                    const authError = error as {error?: string}

                    if (authError?.error === "consent_required" || authError?.error === "login_required") {
                        await loginWithRedirect({
                            appState: {returnTo: window.location.pathname},
                            authorizationParams: {
                                audience,
                                redirect_uri: window.location.origin
                            }
                        })
                    }

                    throw error
                }
            }

            try {
                setLoading(true)

                const [dispatchToken, teletechToken] = await Promise.all([
                    fetchToken(process.env.REACT_APP_AUTH0_DISPATCH_AUDIENCE),
                    fetchToken(process.env.REACT_APP_AUTH0_TELETECH_AUDIENCE)
                ])

                if (!isMounted) {
                    return
                }

                dispatch(updateToken(dispatchToken ?? ""))
                dispatch(updateTeletechToken(teletechToken ?? ""))
            } catch (error) {
                if (isMounted) {
                    console.error("Failed to fetch Auth0 access tokens", error)
                }
            } finally {
                if (isMounted) {
                    setLoading(false)
                }
            }
        }

        void syncTokens()

        return () => {
            isMounted = false
        }
    }, [dispatch, getAccessTokenSilently, isAuthenticated, isAuthLoading, loginWithRedirect])

    return {loading, token, user}
}
